import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import Chart from "react-apexcharts";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import ZoomModal from "./heatmap/zoomModal";
import { radialDimensionDetails } from "../services/api-services";
import { toastEmitter } from "../utills/utilities";
import { API_RESPONSE } from "../utills/app-constant";
import Modal from "../admin/components/common/Model";
import { CHAT_ICON, MENU_ICON, ZOOM_ICON } from "../utills/image-constant";
import Remark from "../admin/components/Charts/Remark";

const RadialDimension = ({ selectedCompanyId, selectedCampaignId, type, graphName, title = "Radial" }) => {
  const [rowData, setRowData] = useState([]);
  const [totalCurrent, setTotalCurrent] = useState(0);
  const [totalFuture, setTotalFuture] = useState(0);
  const [showRemarkModal, setShowRemarkModal] = useState(false);
  const [remarkText, setRemarkText] = useState("");
  const [isAttempt, setIsAttempt] = useState("");
  const [remark, setRemark] = useState("");
  const [remarkId, setRemarkId] = useState(null);
  const [activeDimension, setActiveDimension] = useState(null);
  const modalRef = useRef(null);
  const [dimensionNames, setDimensionNames] = useState([]);
  const [currentValues, setCurrentValues] = useState([]);
  const [futureValues, setFutureValues] = useState([]);
  const [importanceValues, setImportanceValues] = useState([]);
  const [totalImportance, setTotalImportance] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showTextBox, setShowTextBox] = useState(false);
  const [note, setNote] = useState("");
  // const modalId = `zoomModal-${title.replace(/\s+/g, "_")}`;
  const modalId = `zoomModal-${title.replace(/[^a-zA-Z0-9_-]/g, "_")}`;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await radialDimensionDetails(selectedCompanyId, selectedCampaignId, type, graphName);
      if (response?.data?.responseCode === 200) {
        const dims = response.data.responseBody || [];
        setRemark(response?.data?.remark || "");
        setIsAttempt(response?.data?.isAttempt ?? 0);
        setRemarkId(response?.data?.remarkId || null);

        setRowData(dims);
        setDimensionNames(dims.map(d => String(d.dimensionName)));
        setCurrentValues(dims.map(d => Number(d.currentAverageValue) || 0));
        setFutureValues(dims.map(d => Number(d.futureAverageValue) || 0));
        setImportanceValues(dims.map(d => Number(d.importantAverageValue) || 0));

        setTotalImportance(response?.data?.totalImportanceAverage || 0);
        setTotalCurrent(response?.data?.totalCurrentAverage || 0);
        setTotalFuture(response?.data?.totalFutureAverage || 0);
      } else if (response?.data?.responseCode === 404) {
        setRowData([]);
      } else {
        toastEmitter("error", response?.data?.message);
      }
    } catch {
      toastEmitter("error", API_RESPONSE.MESSAGE_503);
    } finally {
      setLoading(false);
    }
  }, [selectedCompanyId, selectedCampaignId, type, graphName]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const chartData = useMemo(() => ({
    options: {
      chart: { type: "radar" },
      xaxis: { categories: dimensionNames },
      stroke: { width: 2, colors: ["#557D39", "#F7BB1C", "#D42327"] },
      fill: { opacity: 0 },
      markers: { size: 4 },
      tooltip: { y: { formatter: val => `${val.toFixed(2)}%` } },
      legend: { position: "top", horizontalAlign: "center" },
    },
    series: [
      { name: "Current Score", data: currentValues },
      { name: "Future Score", data: futureValues },
      { name: "Importance Score", data: importanceValues },
    ]
  }), [dimensionNames, currentValues, futureValues, importanceValues]);

  const exportAsExcel = () => {
    const headers = ["Dimension", "Current", "Future", "Importance"];
    const rows = rowData.map(d => [
      d.dimensionName,
      d.currentAverageValue,
      d.futureAverageValue,
      d.importantAverageValue
    ]);
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
    const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `${title}.xlsx`);
  };

  const exportAsPDF = async () => {
  const chartBox = document.getElementById(`chart-box-${title}`);
  if (!chartBox) return;
  chartBox.scrollTo(0, 0);
  await new Promise(r => setTimeout(r, 200));
 
  const chartElement = chartBox.querySelector(".apexcharts-canvas");
  if (!chartElement) return;
 
  const canvas = await html2canvas(chartElement, {
    scale: 2,
    backgroundColor: "#fff",
    useCORS: true,
  });
 
  const imgData = canvas.toDataURL("image/png");
  const pdf = new jsPDF("l", "mm", "a4");
 
  const pdfWidth = pdf.internal.pageSize.getWidth(); // Width of the PDF
  const pdfHeight = pdf.internal.pageSize.getHeight(); // Height of the PDF
 
  const canvasWidth = canvas.width; // Width of the canvas image
  const canvasHeight = canvas.height; // Height of the canvas image
 
  // Scaling factor to make the image a bit smaller (e.g., scale down by 80%)
  const scaleFactor = Math.min(pdfWidth / canvasWidth, pdfHeight / canvasHeight) * 0.8; // 0.8 reduces the size to 80%
 
  const imgWidth = canvasWidth * scaleFactor; // Scaled width
  const imgHeight = canvasHeight * scaleFactor; // Scaled height
 
  // Calculate the offset to center the image
  const xOffset = (pdfWidth - imgWidth) / 2;
  const yOffset = (pdfHeight - imgHeight) / 2;
 
  // Add the image to the PDF, centered and scaled down
  pdf.addImage(imgData, "PNG", xOffset, yOffset, imgWidth, imgHeight);
 
  // Save the PDF
  pdf.save(`${title || "chart"}.pdf`);
};

const hideDropdownBeforeExport = () => {
    const dropdown = document.querySelector(".dropdown-menu.show");
    if (dropdown) dropdown.classList.remove("show");
  };
 const exportAsImage = async () => {
    hideDropdownBeforeExport();
    const chartBox = document.getElementById(`chart-box-${title}`);
    if (!chartBox) return;
    chartBox.scrollTo(0, 0);
    await new Promise(r => setTimeout(r, 200));

    const chartElement = chartBox.querySelector(".apexcharts-canvas");
    if (!chartElement) return;

    const canvas = await html2canvas(chartElement, {
      scale: 2,
      backgroundColor: "#fff",
      useCORS: true,
    });

    const link = document.createElement("a");
    link.download = `${title || "chart"}.png`;
    link.href = canvas.toDataURL();
    link.click();
  };
  return (
    <div id={`chart-box-${title}`} className="card h-100 ">
      <div className="d-flex justify-content-between align-items-center overall-text mb-2 p-2">
        <h5>{title}</h5>
        <div className="d-flex align-items-center">
          <button className="icon-btn border-0 bg-transparent me-2" data-bs-toggle="modal" data-bs-target={`#${modalId}`}>
            {/* <i className="bi bi-zoom-in"></i> */}
            <img src={ZOOM_ICON} alt="zoom" />

          </button>
          <div className="dropdown">
            <button className="icon-btn bg-transparent" type="button" data-bs-toggle="dropdown">
              {/* <i className="bi bi-three-dots"></i> */}
              <img src={MENU_ICON} alt="menu" />

            </button>
            <ul className="dropdown-menu dropdown-menu-end">
              <li><button className="dropdown-item" onClick={exportAsExcel}>Download Excel</button></li>
              <li><button className="dropdown-item" onClick={exportAsPDF}>Export PDF</button></li>
              <li><button className="dropdown-item" onClick={exportAsImage}>Download Image</button></li>
            </ul>
          </div>
        </div>
      </div>
        <div id={`chart-box-${title}`} className="overallParticipation">

      {loading ? (
        <div className="loader-container"><div className="loader"></div></div>
      ) : (
        <Chart options={chartData.options} series={chartData.series} type="radar" height={500} />
      )}
      </div>
      <div className="d-flex justify-content-around rounded my-3">
        <div className="text-center">
          <h6>Total Current</h6>
          <h5 className="text-success">{totalCurrent?.toFixed(2)}</h5>
        </div>
        <div className="text-center">
          <h6>Total Future</h6>
          <h5 className="text-warning">{totalFuture?.toFixed(2)}</h5>
        </div>
        <div className="text-center">
          <h6>Total Importance</h6>
          <h5 className="text-danger">{totalImportance?.toFixed(2)}</h5>
        </div>
      </div>
      <div className="d-flex justify-content-end mb-2">
        <button
          title="Add Comment"
          onClick={() => {
            setShowRemarkModal(true);
            setTimeout(() => {
              if (modalRef.current) modalRef.current.click();
            }, 0);
          }}
          className="border-0 bg-transparent position-absolute"
          style={{
            bottom: "10px",
            right: "10px",

          }}
        >
          {/* <i className="bi bi-chat-dots"></i> */}
          <img src={CHAT_ICON} alt="zoom" />


        </button>
      </div>

      {/* Remark Modal */}
      {showRemarkModal && (
        <Modal modalClick={modalRef} title="Remark" buttonLable="">
          {/* <textarea
            className="form-control"
            rows={4}
            placeholder={`Add remark`}
            value={remarkText}
            onChange={(e) => setRemarkText(e.target.value)}
          />
          <div className="text-end mt-3">
            <button
              className="btn btn-secondary me-2"
              data-bs-dismiss="modal"
              onClick={() => {
                setShowRemarkModal(false);
              }}

            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={() => {
                toastEmitter("success", `Remark added`);
                setShowRemarkModal(false);
              }}
            >
              Add Remark
            </button>
          </div> */}.

          <Remark
            selectedCompanyId={selectedCompanyId}
            selectedCampaignId={selectedCampaignId}
            type={type}
            graphName={graphName}
            initialRemark={remark || ""}
            remarkId={remarkId || ""}
            isAttempt={isAttempt || 0}
            setRemarkprops={setRemark}
            setRemarkIdProps={setRemarkId}
            onCloseModal={() => setShowRemarkModal(false)}
          />
        </Modal>
      )}

      <ZoomModal
        id={modalId} title={title} chartOptions={chartData.options}
        seriesData={chartData.series} height={400}
        remarkText={remarkText}
        setRemarkText={setRemarkText}
        showRemarkModal={showRemarkModal}
        setShowRemarkModal={setShowRemarkModal}
        modalRef={modalRef}
        exportSummaryToExcel={exportAsExcel}
        exportAsPDF={exportAsPDF}
        exportAsImage={exportAsImage}
      />
    </div>
  );
};

export default RadialDimension;
